import { useEffect, useState } from 'react';
import { useAxios } from '../../utils/hook/useAxios';

/**
 * InsurancePicker
 * Props:
 *  - nombreJours  : number of rental days (for fee calculation)
 *  - reservationId: if provided, saves choice via API on select
 *  - onSelect     : callback(plan | null, fee) when selection changes
 *  - selectedPlanId: controlled selection (optional)
 */
function InsurancePicker({ nombreJours = 1, reservationId, onSelect, selectedPlanId }) {
  const [plans, setPlans] = useState([]);
  const [chosen, setChosen] = useState(selectedPlanId ?? null);
  const [saving, setSaving] = useState(false);
  const axios = useAxios();

  useEffect(() => {
    axios
      .get('/insurance/plans')
      .then((r) => setPlans(r.data || []))
      .catch(() => {});
  }, []);

  const getFee = (plan) => {
    const fee = plan.pricePerDay * nombreJours;
    return Math.round(fee * 100) / 100;
  };

  const handleSelect = async (plan) => {
    const newId = chosen === plan.id ? null : plan.id;
    setChosen(newId);
    const fee = newId ? getFee(plan) : 0;

    if (reservationId) {
      setSaving(true);
      try {
        await axios.post(`/insurance/reservations/${reservationId}/select`, {
          planId: newId,
          nombreJours,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setSaving(false);
      }
    }

    onSelect?.(newId ? plan : null, fee);
  };

  if (plans.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <i className="bi bi-shield-check text-primary" style={{ fontSize: 20 }}></i>
        <div>
          <p className="mb-0 fw-bold" style={{ fontSize: 15 }}>
            Protection & Assurance
          </p>
          <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
            Choisissez votre niveau de couverture pour cette location
          </p>
        </div>
      </div>

      <div className="d-flex flex-column gap-2">
        {plans.map((plan) => {
          const isSelected = chosen === plan.id;
          const fee = getFee(plan);
          const isFree = plan.pricePerDay === 0;

          return (
            <div
              key={plan.id}
              className={`rounded-3 border p-3 cursor-pointer ${isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-white'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleSelect(plan)}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex gap-2">
                  <div className="mt-1">
                    <div
                      className={`rounded-circle border d-flex align-items-center justify-content-center flex-shrink-0 ${isSelected ? 'border-primary bg-primary' : 'border-secondary'}`}
                      style={{ width: 18, height: 18 }}
                    >
                      {isSelected && (
                        <i
                          className="bi bi-check text-white"
                          style={{ fontSize: 11, lineHeight: 1 }}
                        ></i>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
                      {plan.name}
                      {isFree && (
                        <span className="badge bg-success ms-2" style={{ fontSize: 10 }}>
                          Inclus
                        </span>
                      )}
                    </p>
                    <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                      {plan.description}
                    </p>
                    {Array.isArray(plan.coverageDetails) && plan.coverageDetails.length > 0 && (
                      <ul className="mb-0 mt-1 ps-3" style={{ fontSize: 11, color: '#555' }}>
                        {plan.coverageDetails.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {plan.franchise > 0 && (
                      <p className="mb-0 mt-1 text-muted" style={{ fontSize: 11 }}>
                        Franchise : <strong>{plan.franchise.toLocaleString('fr-FR')} €</strong>
                      </p>
                    )}
                  </div>
                </div>

                <div className="text-end flex-shrink-0 ms-2">
                  {isFree ? (
                    <span className="fw-bold text-success" style={{ fontSize: 14 }}>
                      Gratuit
                    </span>
                  ) : (
                    <div>
                      <span className="fw-bold" style={{ fontSize: 15 }}>
                        +{fee.toFixed(2)} €
                      </span>
                      <br />
                      <span className="text-muted" style={{ fontSize: 11 }}>
                        {plan.pricePerDay.toFixed(2)} €/jour
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {saving && (
        <p className="text-muted small mt-2">
          <span className="spinner-border spinner-border-sm me-1" />
          Enregistrement…
        </p>
      )}
    </div>
  );
}

export default InsurancePicker;
